import { createContext, useContext, useState, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { alert } from "@/contexts/AlertModalContext";
import inovaone_api from "@/services/inovaone_api";
import type { LocalizacaoResponse } from "@/types/localizacoes-response";
import {
  aninharLocalizacoes,
  type LocalizacaoNv1Node,
} from "@/utils/UtilFuncoes";
import type { newLocalizacaoNv1 } from "@/types/new-localizacao-nv1";
import type { LocalizacaoItem } from "@/types/localizacao-nv1-item";
import type { newLocalizacaoNv2 } from "@/types/new-localizacao-nv2";
import type { LocalizacaoNv2Item } from "@/types/localizacao-nv2-item";
import type { LocalizacaoNv3Item } from "@/types/localizacao-nv3-item";
import type { newLocalizacaoNv3 } from "@/types/new-localizacao-nv3";
import type { newLocalizacaoNv4 } from "@/types/new-localizacao-nv4";
import type { LocalizacaoNv4Item } from "@/types/localizacao-nv4-item";

export type LocalizacoesContextType = {
  localizacoes: LocalizacaoNv1Node[];
  coletaLocalizacoes: () => Promise<void>;
  criarLocalizacoesNV1: (
    localizacoes_nv1: newLocalizacaoNv1[],
  ) => Promise<LocalizacaoItem[] | undefined>;
  criarLocalizacoesNV2: (
    localizacoes_nv2: newLocalizacaoNv2[],
  ) => Promise<LocalizacaoNv2Item[] | undefined>;
  criarLocalizacoesNV3: (
    localizacoes_nv3: newLocalizacaoNv3[],
  ) => Promise<LocalizacaoNv3Item[] | undefined>;
  criarLocalizacoesNV4: (
    localizacoes_nv4: newLocalizacaoNv4[],
  ) => Promise<LocalizacaoNv4Item[] | undefined>;
};

export const LocalizacoesContext = createContext<
  LocalizacoesContextType | undefined
>(undefined);

export function LocalizacoesProvider({ children }: { children: ReactNode }) {
  const { usuario } = useAuth();
  const [localizacoes, setLocalizacoes] = useState<LocalizacaoNv1Node[]>([]);

  const criarLocalizacoesNV1 = async (
    localizacoes_nv1: newLocalizacaoNv1[],
  ) => {
    try {
      const response_criar_localizacoes_nv1 = await inovaone_api.post<
        LocalizacaoItem[]
      >(`/nivel_loc1`, localizacoes_nv1);
      coletaLocalizacoes();
      return response_criar_localizacoes_nv1.data;
    } catch (error) {
      alert.error(
        "Erro ao criar localizações de nivel 1. Tente novamente mais tarde.",
      );
    }
  };

  const criarLocalizacoesNV2 = async (
    localizacoes_nv2: newLocalizacaoNv2[],
  ) => {
    try {
      const response_criar_localizacoes_nv2 = await inovaone_api.post<
        LocalizacaoNv2Item[]
      >(`/nivel_loc2`, localizacoes_nv2);
      coletaLocalizacoes();
      return response_criar_localizacoes_nv2.data;
    } catch (error) {
      alert.error(
        "Erro ao criar localizações de nivel 2. Tente novamente mais tarde.",
      );
    }
  };

  const criarLocalizacoesNV3 = async (
    localizacoes_nv3: newLocalizacaoNv3[],
  ) => {
    try {
      const response_criar_localizacoes_nv3 = await inovaone_api.post<
        LocalizacaoNv3Item[]
      >(`/nivel_loc3`, localizacoes_nv3);
      coletaLocalizacoes();
      return response_criar_localizacoes_nv3.data;
    } catch (error) {
      alert.error(
        "Erro ao criar localizações de nivel 3. Tente novamente mais tarde.",
      );
    }
  };

  const criarLocalizacoesNV4 = async (
    localizacoes_nv4: newLocalizacaoNv4[],
  ) => {
    try {
      const response_criar_localizacoes_nv4 = await inovaone_api.post<
        LocalizacaoNv4Item[]
      >(`/nivel_loc4`, localizacoes_nv4);
      coletaLocalizacoes();
      return response_criar_localizacoes_nv4.data;
    } catch (error) {
      alert.error(
        "Erro ao criar localizações de nivel 4. Tente novamente mais tarde.",
      );
    }
  };

  const coletaLocalizacoes = async () => {
    try {
      const response_localizacoes = await inovaone_api.get<LocalizacaoResponse>(
        `/localizacoes/${usuario?.id_conta}`,
      );
      setLocalizacoes(aninharLocalizacoes(response_localizacoes.data));
    } catch (error) {
      console.error("Erro ao coletar localizações:", error);
      alert.error("Erro ao coletar localizações. Tente novamente mais tarde.");
    }
  };

  return (
    <LocalizacoesContext.Provider
      value={{
        localizacoes,
        coletaLocalizacoes,
        criarLocalizacoesNV1,
        criarLocalizacoesNV2,
        criarLocalizacoesNV3,
        criarLocalizacoesNV4,
      }}
    >
      {children}
    </LocalizacoesContext.Provider>
  );
}

export function useLocalizacoes() {
  const context = useContext(LocalizacoesContext);
  if (!context) {
    throw new Error(
      "useLocalizacoes deve ser usado dentro de um LocalizacoesProvider",
    );
  }
  return context;
}
